import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/utils';
import { CreateOpportunityModal } from '../opportunities/CreateOpportunityModal';
import { 
  X, 
  Radar, 
  BarChart3, 
  Brain, 
  Target, 
  FileText,
  CheckCircle,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Clock,
  Users,
  Sparkles,
  Zap,
  ChevronRight,
  Phone,
  FileEdit,
  Eye,
  ArrowRight,
  Shield,
  Lightbulb,
  Building2
} from 'lucide-react';

const SCAN_STEPS = [
  { id: 'signals', label: 'Scanning Market Signals', icon: Radar, duration: 1200 },
  { id: 'pipeline', label: 'Analyzing Pipeline', icon: BarChart3, duration: 1000 },
  { id: 'intelligence', label: 'Applying Buyer Intelligence', icon: Brain, duration: 1100 },
  { id: 'opportunities', label: 'Identifying Opportunities', icon: Target, duration: 900 },
  { id: 'actions', label: 'Generating Action Plan', icon: FileText, duration: 800 },
];

export function FullOpportunityScan({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { get } = useApi();
  const [scanPhase, setScanPhase] = useState('idle'); // idle, scanning, complete
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSignalForCreate, setSelectedSignalForCreate] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScanPhase('idle');
      setCurrentStep(0);
      setCompletedSteps([]);
      setScanResults(null);
      setError(null);
    }
  }, [isOpen]);

  // Start scan
  const startScan = async () => {
    setScanPhase('scanning');
    setCurrentStep(0);
    setCompletedSteps([]);
    setError(null);

    try {
      // Run through animated steps while fetching data
      for (let i = 0; i < SCAN_STEPS.length; i++) {
        setCurrentStep(i);
        await new Promise(resolve => setTimeout(resolve, SCAN_STEPS[i].duration));
        setCompletedSteps(prev => [...prev, SCAN_STEPS[i].id]);
      }

      // Fetch all data in parallel
      const [signals, opportunities, dashboardStats, momentumCheck] = await Promise.all([
        get('/market-intelligence/signals').catch(() => []),
        get('/opportunities').catch(() => []),
        get('/dashboard/stats').catch(() => ({})),
        get('/dashboard/momentum-check').catch(() => ({})),
      ]);

      // Process and analyze data
      const results = analyzeData(signals, opportunities, dashboardStats, momentumCheck);
      setScanResults(results);
      setScanPhase('complete');
    } catch (err) {
      console.error('Scan failed:', err);
      setError('Failed to complete scan. Please try again.');
      setScanPhase('idle');
    }
  };

  // Analyze all the data and generate insights
  const analyzeData = (signals, opportunities, stats, momentum) => {
    const signalList = Array.isArray(signals) ? signals : signals?.signals || [];
    const oppList = Array.isArray(opportunities) ? opportunities : opportunities?.opportunities || [];
    
    // Find top opportunity (highest value unconverted signal)
    const unconvertedSignals = signalList.filter(s => 
      s.signal_category === 'market' && !s.linked_opportunity_id
    );
    
    const scoredSignals = unconvertedSignals.map(s => ({
      ...s,
      score: calculateSignalScore(s)
    })).sort((a, b) => b.score - a.score);

    const topSignal = scoredSignals[0] || null;

    // Find pipeline risks (stalled deals)
    const stalledDeals = oppList.filter(opp => {
      if (!opp.updated_at) return false;
      const daysSinceUpdate = Math.floor((Date.now() - new Date(opp.updated_at).getTime()) / (1000 * 60 * 60 * 24));
      return daysSinceUpdate > 14 && !['closed_won', 'closed_lost'].includes(opp.stage);
    }).slice(0, 3);

    // Find competitive signals
    const competitiveSignals = signalList.filter(s => 
      s.signal_type === 'competitor_activity' || 
      s.description?.toLowerCase().includes('competitor') ||
      s.description?.toLowerCase().includes('competition')
    ).slice(0, 2);

    // Calculate strategic insights
    const totalPipelineValue = stats?.total_pipeline_value || 0;
    const activeOpps = stats?.active_opportunities || oppList.length;
    const signalCount = signalList.length;
    const highValueOpps = oppList.filter(o => (o.estimated_value || 0) > 50000);

    // Generate strategic insight
    let strategicInsight = '';
    if (topSignal && stalledDeals.length === 0) {
      strategicInsight = `Your pipeline is healthy with ${activeOpps} active opportunities worth ${formatCurrency(totalPipelineValue)}. Focus on converting the ${unconvertedSignals.length} market signals into new deals to maintain momentum.`;
    } else if (stalledDeals.length > 2) {
      strategicInsight = `${stalledDeals.length} deals need attention - they've been inactive for over 2 weeks. Re-engage these contacts before they go cold. Your market radar has ${unconvertedSignals.length} new signals waiting.`;
    } else if (unconvertedSignals.length > 5) {
      strategicInsight = `You have ${unconvertedSignals.length} market signals that haven't been converted yet. Prioritize the highest-scoring signals to capture emerging opportunities before competitors.`;
    } else {
      strategicInsight = `Pipeline health is moderate with ${formatCurrency(totalPipelineValue)} in motion across ${activeOpps} opportunities. Consider expanding your market signal coverage to identify new prospects.`;
    }

    return {
      topOpportunity: topSignal ? {
        signal: topSignal,
        score: topSignal.score,
        reason: getTopOpportunityReason(topSignal)
      } : null,
      pipelineRisks: stalledDeals.map(deal => ({
        ...deal,
        daysSinceUpdate: Math.floor((Date.now() - new Date(deal.updated_at).getTime()) / (1000 * 60 * 60 * 24)),
        riskLevel: getRiskLevel(deal)
      })),
      competitiveSignals,
      strategicInsight,
      stats: {
        totalPipelineValue,
        activeOpportunities: activeOpps,
        unconvertedSignals: unconvertedSignals.length,
        highValueDeals: highValueOpps.length
      }
    };
  };

  const calculateSignalScore = (signal) => {
    let score = 50; // Base score
    
    // Signal strength
    if (signal.signal_strength === 'high') score += 30;
    else if (signal.signal_strength === 'medium') score += 15;
    
    // Signal confidence
    if (signal.signal_confidence === 'high') score += 20;
    else if (signal.signal_confidence === 'medium') score += 10;
    
    // Recency (signals from last 7 days get bonus)
    if (signal.created_at) {
      const daysOld = Math.floor((Date.now() - new Date(signal.created_at).getTime()) / (1000 * 60 * 60 * 24));
      if (daysOld < 7) score += 15;
      else if (daysOld < 14) score += 5;
    }
    
    // Signal type bonuses
    const highValueTypes = ['new_hotel', 'renovation', 'expansion', 'acquisition', 'territory_expansion'];
    if (highValueTypes.includes(signal.signal_type)) score += 10;
    
    return Math.min(score, 100);
  };

  const getTopOpportunityReason = (signal) => {
    const reasons = [];
    if (signal.signal_strength === 'high') reasons.push('Strong market signal');
    if (signal.signal_confidence === 'high') reasons.push('High confidence data');
    if (signal.signal_type === 'new_hotel') reasons.push('New property opportunity');
    if (signal.signal_type === 'renovation') reasons.push('Renovation project');
    if (signal.signal_type === 'expansion') reasons.push('Expansion initiative');
    return reasons.length > 0 ? reasons.join(' • ') : 'Emerging opportunity';
  };

  const getRiskLevel = (deal) => {
    const daysSinceUpdate = Math.floor((Date.now() - new Date(deal.updated_at).getTime()) / (1000 * 60 * 60 * 24));
    if (daysSinceUpdate > 30) return 'high';
    if (daysSinceUpdate > 21) return 'medium';
    return 'low';
  };

  // Action handlers
  const handleCreateOpportunity = (signal) => {
    setSelectedSignalForCreate(signal);
    setShowCreateModal(true);
  };

  const handleOpportunityCreated = (opportunity) => {
    // Refresh scan results or close
    onClose();
    navigate(`/opportunity/${opportunity.id}`);
  };

  const handleViewSignal = (signalId) => {
    onClose();
    navigate(`/market-intelligence?highlight=${signalId}`);
  };

  const handleGenerateCallBrief = (oppId) => {
    onClose();
    navigate(`/opportunity/${oppId}?action=call-brief`);
  };

  const handleGenerateVisionProposal = (oppId) => {
    onClose();
    navigate(`/opportunity/${oppId}?action=vision-deck`);
  };

  const handleViewOpportunity = (oppId) => {
    onClose();
    navigate(`/opportunity/${oppId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm animate-fade-in" data-testid="full-scan-overlay">
      <div className="absolute inset-0 overflow-y-auto">
        <div className="min-h-full flex flex-col">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-b from-[#1A1A1A] to-[#1A1A1A]/95 border-b border-white/10 px-6 py-4">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#C9A227]/20">
                  <Zap className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Full Opportunity Scan</h2>
                  <p className="text-sm text-gray-400">AI-powered pipeline intelligence</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onClose}
                className="text-gray-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-8">
            <div className="max-w-5xl mx-auto">
              {scanPhase === 'idle' && (
                <div className="text-center py-16 animate-fade-in">
                  <div className="relative w-32 h-32 mx-auto mb-8">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#C9A227]/20 to-transparent animate-pulse" />
                    <div className="absolute inset-4 rounded-full bg-gradient-to-br from-[#C9A227]/30 to-transparent" />
                    <div className="absolute inset-8 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <Radar className="w-10 h-10 text-[#C9A227]" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Ready to Scan</h3>
                  <p className="text-gray-400 max-w-md mx-auto mb-8">
                    Analyze your market signals, pipeline health, and buyer intelligence to identify your next best moves.
                  </p>
                  <Button 
                    onClick={startScan}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white px-8 py-6 text-lg"
                    data-testid="start-scan-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Start Opportunity Scan
                  </Button>
                </div>
              )}

              {scanPhase === 'scanning' && (
                <div className="py-12 animate-fade-in">
                  <div className="max-w-md mx-auto">
                    <div className="text-center mb-8">
                      <Loader2 className="w-12 h-12 text-[#C9A227] animate-spin mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white">Scanning Your Pipeline</h3>
                      <p className="text-gray-400 text-sm mt-2">This will only take a moment...</p>
                    </div>
                    
                    <div className="space-y-3">
                      {SCAN_STEPS.map((step, index) => {
                        const StepIcon = step.icon;
                        const isComplete = completedSteps.includes(step.id);
                        const isCurrent = currentStep === index && !isComplete;
                        
                        return (
                          <div 
                            key={step.id}
                            className={`flex items-center gap-4 p-4 rounded-lg transition-all duration-300 ${
                              isComplete ? 'bg-emerald-500/10 border border-emerald-500/30' :
                              isCurrent ? 'bg-[#C9A227]/10 border border-[#C9A227]/30' :
                              'bg-white/5 border border-white/10'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${
                              isComplete ? 'bg-emerald-500/20' :
                              isCurrent ? 'bg-[#C9A227]/20' :
                              'bg-white/10'
                            }`}>
                              {isComplete ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                              ) : isCurrent ? (
                                <Loader2 className="w-5 h-5 text-[#C9A227] animate-spin" />
                              ) : (
                                <StepIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </div>
                            <span className={`text-sm font-medium ${
                              isComplete ? 'text-emerald-400' :
                              isCurrent ? 'text-[#C9A227]' :
                              'text-gray-500'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {scanPhase === 'complete' && scanResults && (
                <div className="space-y-6 animate-fade-in pb-24">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-white">{formatCurrency(scanResults.stats.totalPipelineValue)}</div>
                        <div className="text-xs text-gray-400">Pipeline Value</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-white">{scanResults.stats.activeOpportunities}</div>
                        <div className="text-xs text-gray-400">Active Deals</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-[#C9A227]">{scanResults.stats.unconvertedSignals}</div>
                        <div className="text-xs text-gray-400">New Signals</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-emerald-400">{scanResults.stats.highValueDeals}</div>
                        <div className="text-xs text-gray-400">High Value Deals</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Opportunity */}
                  {scanResults.topOpportunity && (
                    <Card className="bg-gradient-to-br from-[#C9A227]/10 to-transparent border-[#C9A227]/30">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-[#C9A227]" />
                          <CardTitle className="text-white text-lg">Top Opportunity</CardTitle>
                          <Badge className="bg-[#C9A227] text-white ml-auto">
                            Score: {scanResults.topOpportunity.score}/100
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-xl font-semibold text-white mb-1">
                              {scanResults.topOpportunity.signal.company_name || scanResults.topOpportunity.signal.title}
                            </h4>
                            <p className="text-sm text-[#C9A227]">{scanResults.topOpportunity.reason}</p>
                            {scanResults.topOpportunity.signal.description && (
                              <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                                {scanResults.topOpportunity.signal.description}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              onClick={() => handleCreateOpportunity(scanResults.topOpportunity.signal)}
                              className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                              data-testid="create-opp-from-scan"
                            >
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Create Opportunity
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => handleViewSignal(scanResults.topOpportunity.signal.id)}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Signal
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pipeline Risks */}
                  {scanResults.pipelineRisks.length > 0 && (
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                          <CardTitle className="text-white text-lg">Pipeline Risks</CardTitle>
                          <Badge variant="outline" className="text-amber-400 border-amber-400/30 ml-auto">
                            {scanResults.pipelineRisks.length} deals need attention
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {scanResults.pipelineRisks.map((deal, idx) => (
                            <div 
                              key={deal.id || idx}
                              className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  deal.riskLevel === 'high' ? 'bg-red-400' :
                                  deal.riskLevel === 'medium' ? 'bg-amber-400' :
                                  'bg-yellow-400'
                                }`} />
                                <div>
                                  <h5 className="text-white font-medium">{deal.company_name || 'Unnamed Deal'}</h5>
                                  <div className="flex items-center gap-2 text-xs text-gray-400">
                                    <Clock className="w-3 h-3" />
                                    <span>{deal.daysSinceUpdate} days inactive</span>
                                    <span className="text-gray-600">•</span>
                                    <span>{formatCurrency(deal.estimated_value || 0)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleGenerateCallBrief(deal.id)}
                                  className="border-white/20 text-white hover:bg-white/10"
                                >
                                  <Phone className="w-3 h-3 mr-1" />
                                  Call Brief
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => handleViewOpportunity(deal.id)}
                                  className="bg-white/10 hover:bg-white/20 text-white"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Competitive Signals */}
                  {scanResults.competitiveSignals.length > 0 && (
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-blue-400" />
                          <CardTitle className="text-white text-lg">Competitive Signals</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {scanResults.competitiveSignals.map((signal, idx) => (
                            <div 
                              key={signal.id || idx}
                              className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div>
                                <h5 className="text-white font-medium">{signal.title || signal.company_name}</h5>
                                <p className="text-sm text-gray-400 line-clamp-1">{signal.description}</p>
                              </div>
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewSignal(signal.id)}
                                className="border-white/20 text-white hover:bg-white/10"
                              >
                                View
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Strategic Insight */}
                  <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-emerald-400" />
                        <CardTitle className="text-white text-lg">Strategic Insight</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-300 leading-relaxed">{scanResults.strategicInsight}</p>
                    </CardContent>
                  </Card>

                  {/* No Results State */}
                  {!scanResults.topOpportunity && scanResults.pipelineRisks.length === 0 && (
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="py-12 text-center">
                        <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                        <h4 className="text-xl font-semibold text-white mb-2">Pipeline Looking Good!</h4>
                        <p className="text-gray-400">No immediate actions required. Keep monitoring your market signals for new opportunities.</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-400">{error}</p>
                  <Button 
                    onClick={startScan}
                    variant="outline"
                    className="mt-4 border-white/20 text-white hover:bg-white/10"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sticky Bottom CTA */}
          {scanPhase === 'complete' && scanResults && (
            <div className="sticky bottom-0 z-20 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A] to-transparent pt-8 pb-6 px-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-[#C9A227]/20 to-[#C9A227]/10 border border-[#C9A227]/30">
                  <div className="flex items-center gap-3">
                    <Zap className="w-6 h-6 text-[#C9A227]" />
                    <div>
                      <h4 className="text-white font-semibold">Ready to Take Action?</h4>
                      <p className="text-sm text-gray-400">Convert insights into results</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline"
                      onClick={onClose}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="close-scan-btn"
                    >
                      Close
                    </Button>
                    {scanResults.topOpportunity && (
                      <Button 
                        onClick={() => handleCreateOpportunity(scanResults.topOpportunity.signal)}
                        className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Take Action Now
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Opportunity Modal */}
      <CreateOpportunityModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedSignalForCreate(null);
        }}
        signal={selectedSignalForCreate}
        onSuccess={handleOpportunityCreated}
      />
    </div>
  );
}
